export interface DDQQuestion {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'boolean';
  options?: string[];
  placeholder?: string;
  required?: boolean;
}

export interface DDQSection {
  key: string;
  title: string;
  description: string;
  questions: DDQQuestion[];
}

const firmOverview: DDQSection = {
  key: 'firm_overview',
  title: 'Firm Overview',
  description: 'Basic information about your firm and regulatory status.',
  questions: [
    { key: 'legal_name', label: 'Legal entity name', type: 'text', required: true },
    { key: 'trading_name', label: 'Trading name (if different)', type: 'text' },
    { key: 'legal_structure', label: 'Legal structure', type: 'select', options: ['Limited Partnership', 'LLC', 'LLP', 'Limited Company', 'Other'] },
    { key: 'jurisdiction', label: 'Jurisdiction of incorporation', type: 'text', placeholder: 'e.g. Cayman Islands' },
    { key: 'founded_year', label: 'Year founded', type: 'text', placeholder: 'e.g. 2015' },
    { key: 'headquarters', label: 'Headquarters address', type: 'textarea' },
    { key: 'regulator', label: 'Primary regulator', type: 'select', options: ['FCA', 'SEC', 'MAS', 'FINRA', 'CSSF', 'DFSA', 'ASIC', 'Other', 'Unregulated'] },
    { key: 'registration_number', label: 'Regulatory registration number', type: 'text' },
    { key: 'other_regulators', label: 'Other regulatory registrations', type: 'textarea', placeholder: 'List any additional regulatory registrations' },
    { key: 'firm_aum', label: 'Total firm AUM (USD mm)', type: 'text' },
    { key: 'aum_date', label: 'AUM as of date', type: 'text', placeholder: 'e.g. 31 March 2026' },
    { key: 'num_employees', label: 'Total number of employees', type: 'text' },
    { key: 'num_investment_professionals', label: 'Number of investment professionals', type: 'text' },
    { key: 'website', label: 'Website', type: 'text' },
    { key: 'firm_description', label: 'Firm description', type: 'textarea', placeholder: 'Brief overview of the firm, its history and mission', required: true },
  ]
};

const keyPeople: DDQSection = {
  key: 'key_people',
  title: 'Key People',
  description: 'Information about the principals and key personnel.',
  questions: [
    { key: 'cio_name', label: 'CIO / Lead Portfolio Manager name', type: 'text', required: true },
    { key: 'cio_bio', label: 'CIO / Lead PM biography', type: 'textarea', placeholder: 'Background, experience, previous roles', required: true },
    { key: 'cio_years_experience', label: 'CIO years of investment experience', type: 'text' },
    { key: 'other_key_people', label: 'Other key personnel', type: 'textarea', placeholder: 'List other key personnel with name, role and brief bio' },
    { key: 'key_person_risk', label: 'Key person risk mitigation', type: 'textarea', placeholder: 'How is key person risk managed?' },
    { key: 'ownership_structure', label: 'Firm ownership structure', type: 'textarea', placeholder: 'Who owns the firm and in what proportions?' },
    { key: 'staff_turnover', label: 'Key staff turnover in last 3 years', type: 'textarea', placeholder: 'Describe any significant departures and reasons' },
  ]
};

const compliance: DDQSection = {
  key: 'compliance',
  title: 'Compliance & Legal',
  description: 'Regulatory, legal and compliance matters.',
  questions: [
    { key: 'regulatory_actions', label: 'Regulatory actions or investigations', type: 'textarea', placeholder: 'Disclose any past or pending regulatory actions, investigations or sanctions', required: true },
    { key: 'litigation', label: 'Litigation history', type: 'textarea', placeholder: 'Disclose any material litigation, past or pending', required: true },
    { key: 'conflicts_of_interest', label: 'Conflicts of interest', type: 'textarea', placeholder: 'Describe any actual or potential conflicts of interest and how they are managed' },
    { key: 'insurance', label: 'Insurance coverage', type: 'textarea', placeholder: 'Describe professional indemnity and D&O insurance coverage' },
    { key: 'aml_kyc', label: 'AML/KYC procedures', type: 'textarea', placeholder: 'Describe your AML/KYC onboarding process for investors' },
    { key: 'compliance_officer', label: 'Compliance officer', type: 'text', placeholder: 'Name and role of compliance officer' },
    { key: 'external_compliance', label: 'External compliance support', type: 'textarea', placeholder: 'Do you use external compliance consultants? If so, who?' },
  ]
};

const operations: DDQSection = {
  key: 'operations',
  title: 'Operations',
  description: 'Service providers and operational infrastructure.',
  questions: [
    { key: 'prime_broker', label: 'Prime broker(s)', type: 'textarea', placeholder: 'Name all prime brokers and services provided' },
    { key: 'custodian', label: 'Custodian', type: 'text' },
    { key: 'fund_administrator', label: 'Fund administrator', type: 'text', required: true },
    { key: 'auditor', label: 'Auditor', type: 'text', required: true },
    { key: 'legal_counsel', label: 'Legal counsel (fund)', type: 'text' },
    { key: 'legal_counsel_firm', label: 'Legal counsel (firm/manager)', type: 'text' },
    { key: 'oms_pms', label: 'OMS / PMS system', type: 'text', placeholder: 'e.g. Bloomberg AIM, Charles River' },
    { key: 'risk_system', label: 'Risk management system', type: 'text' },
    { key: 'cyber_security', label: 'Cyber security measures', type: 'textarea', placeholder: 'Describe key cyber security policies and procedures' },
    { key: 'business_continuity', label: 'Business continuity plan', type: 'textarea', placeholder: 'Describe BCP/DR arrangements' },
  ]
};

const esg: DDQSection = {
  key: 'esg',
  title: 'ESG',
  description: 'Environmental, social and governance policies.',
  questions: [
    { key: 'esg_policy', label: 'ESG policy', type: 'boolean', options: ['Yes', 'No'] },
    { key: 'esg_policy_description', label: 'ESG policy description', type: 'textarea', placeholder: 'Describe your ESG policy and approach' },
    { key: 'esg_exclusions', label: 'ESG exclusions', type: 'textarea', placeholder: 'List any sectors or activities excluded on ESG grounds' },
    { key: 'esg_reporting', label: 'ESG reporting framework', type: 'select', options: ['PRI', 'TCFD', 'SFDR', 'GRI', 'SASB', 'None', 'Other'] },
    { key: 'un_pri_signatory', label: 'UN PRI signatory', type: 'boolean', options: ['Yes', 'No'] },
    { key: 'esg_integration', label: 'ESG integration in investment process', type: 'textarea', placeholder: 'How is ESG integrated into your investment decision making?' },
  ]
};

// Hedge Fund specific sections
const hedgeFundStrategy: DDQSection = {
  key: 'investment_strategy',
  title: 'Investment Strategy',
  description: 'Details of your investment strategy and process.',
  questions: [
    { key: 'strategy_description', label: 'Strategy description', type: 'textarea', required: true, placeholder: 'Detailed description of the investment strategy' },
    { key: 'investment_universe', label: 'Investment universe', type: 'textarea', placeholder: 'Asset classes, geographies, instruments traded' },
    { key: 'idea_generation', label: 'Idea generation process', type: 'textarea', placeholder: 'How are investment ideas sourced and generated?' },
    { key: 'investment_process', label: 'Investment process', type: 'textarea', placeholder: 'Describe the end-to-end investment process from idea to execution' },
    { key: 'edge', label: 'Competitive edge', type: 'textarea', placeholder: 'What is your edge over other market participants?' },
    { key: 'typical_portfolio', label: 'Typical portfolio characteristics', type: 'textarea', placeholder: 'Number of positions, concentration, long/short split, turnover' },
    { key: 'liquidity_profile', label: 'Liquidity profile of portfolio', type: 'textarea', placeholder: 'How liquid is the portfolio? Days to liquidate?' },
  ]
};

const hedgeFundRisk: DDQSection = {
  key: 'risk_management',
  title: 'Risk Management',
  description: 'Risk management framework and controls.',
  questions: [
    { key: 'risk_framework', label: 'Risk management framework', type: 'textarea', required: true, placeholder: 'Describe the overall risk management framework' },
    { key: 'risk_limits', label: 'Risk limits', type: 'textarea', placeholder: 'Position limits, sector limits, VaR limits, drawdown limits' },
    { key: 'var_methodology', label: 'VaR methodology', type: 'select', options: ['Historical simulation', 'Parametric', 'Monte Carlo', 'Not applicable', 'Other'] },
    { key: 'max_drawdown_limit', label: 'Maximum drawdown limit', type: 'text', placeholder: 'e.g. 15% fund-level stop loss' },
    { key: 'leverage', label: 'Leverage usage', type: 'textarea', placeholder: 'Describe use of leverage, gross and net exposure ranges' },
    { key: 'stress_testing', label: 'Stress testing', type: 'textarea', placeholder: 'Describe stress testing and scenario analysis procedures' },
    { key: 'risk_independence', label: 'Risk function independence', type: 'textarea', placeholder: 'Is risk management independent from portfolio management?' },
  ]
};

// PE specific sections
const peStrategy: DDQSection = {
  key: 'investment_strategy',
  title: 'Investment Strategy',
  description: 'Details of your private equity investment approach.',
  questions: [
    { key: 'strategy_description', label: 'Strategy description', type: 'textarea', required: true },
    { key: 'target_sectors', label: 'Target sectors', type: 'textarea', placeholder: 'Primary sectors of focus' },
    { key: 'target_geographies', label: 'Target geographies', type: 'textarea' },
    { key: 'deal_size', label: 'Target deal/company size', type: 'textarea', placeholder: 'e.g. enterprise value, revenue, EBITDA ranges' },
    { key: 'deal_sourcing', label: 'Deal sourcing', type: 'textarea', placeholder: 'How do you source deals? Proprietary vs intermediated?' },
    { key: 'value_creation', label: 'Value creation approach', type: 'textarea', placeholder: 'How do you create value in portfolio companies?' },
    { key: 'typical_hold_period', label: 'Typical hold period', type: 'text', placeholder: 'e.g. 4-7 years' },
    { key: 'exit_strategies', label: 'Exit strategies', type: 'textarea', placeholder: 'Trade sale, IPO, secondary, recapitalisation etc' },
  ]
};

const peTrackRecord: DDQSection = {
  key: 'track_record',
  title: 'Track Record',
  description: 'Historical performance and portfolio overview.',
  questions: [
    { key: 'gross_irr', label: 'Gross IRR (flagship fund)', type: 'text' },
    { key: 'net_irr', label: 'Net IRR (flagship fund)', type: 'text' },
    { key: 'tvpi', label: 'TVPI (flagship fund)', type: 'text' },
    { key: 'dpi', label: 'DPI (flagship fund)', type: 'text' },
    { key: 'num_investments', label: 'Total investments made (all funds)', type: 'text' },
    { key: 'num_realised', label: 'Number of fully realised investments', type: 'text' },
    { key: 'notable_exits', label: 'Notable exits', type: 'textarea', placeholder: 'Describe 2-3 notable realised investments' },
    { key: 'write_offs', label: 'Write-offs / total losses', type: 'textarea', placeholder: 'Describe any total loss investments and circumstances' },
    { key: 'audited_returns', label: 'Are returns audited?', type: 'boolean', options: ['Yes', 'No'] },
  ]
};

// VC specific
const vcStrategy: DDQSection = {
  key: 'investment_strategy',
  title: 'Investment Strategy',
  description: 'Details of your venture capital investment approach.',
  questions: [
    { key: 'strategy_description', label: 'Strategy description', type: 'textarea', required: true },
    { key: 'stage_focus', label: 'Stage focus', type: 'multiselect', options: ['Pre-seed', 'Seed', 'Series A', 'Series B', 'Series C+', 'Growth'] },
    { key: 'sector_focus', label: 'Sector focus', type: 'textarea' },
    { key: 'geographic_focus', label: 'Geographic focus', type: 'textarea' },
    { key: 'check_size', label: 'Initial check size range', type: 'text', placeholder: 'e.g. $500k - $3mm' },
    { key: 'follow_on_policy', label: 'Follow-on investment policy', type: 'textarea' },
    { key: 'deal_sourcing', label: 'Deal sourcing', type: 'textarea' },
    { key: 'portfolio_support', label: 'Portfolio company support', type: 'textarea', placeholder: 'How do you add value beyond capital?' },
    { key: 'board_seats', label: 'Board seat policy', type: 'textarea' },
  ]
};

// Real Estate specific
const reStrategy: DDQSection = {
  key: 'investment_strategy',
  title: 'Investment Strategy',
  description: 'Details of your real estate investment approach.',
  questions: [
    { key: 'strategy_description', label: 'Strategy description', type: 'textarea', required: true },
    { key: 'property_types', label: 'Property types', type: 'multiselect', options: ['Office', 'Retail', 'Industrial/Logistics', 'Residential', 'Hotels', 'Healthcare', 'Mixed-use', 'Data centres', 'Other'] },
    { key: 'geographies', label: 'Target geographies', type: 'textarea' },
    { key: 'risk_profile', label: 'Risk profile', type: 'select', options: ['Core', 'Core-plus', 'Value-add', 'Opportunistic', 'Debt'] },
    { key: 'deal_size', label: 'Target deal size', type: 'text' },
    { key: 'sourcing', label: 'Deal sourcing approach', type: 'textarea' },
    { key: 'asset_management', label: 'Asset management approach', type: 'textarea', placeholder: 'In-house vs outsourced, reporting frequency' },
    { key: 'leverage_policy', label: 'Leverage policy', type: 'textarea', placeholder: 'Target LTV ratios' },
  ]
};

// Credit specific
const creditStrategy: DDQSection = {
  key: 'investment_strategy',
  title: 'Investment Strategy',
  description: 'Details of your credit investment approach.',
  questions: [
    { key: 'strategy_description', label: 'Strategy description', type: 'textarea', required: true },
    { key: 'credit_types', label: 'Credit instrument types', type: 'multiselect', options: ['Senior secured', 'Unitranche', 'Mezzanine', 'Second lien', 'Subordinated', 'Distressed', 'CLO', 'ABS', 'Other'] },
    { key: 'target_sectors', label: 'Target sectors', type: 'textarea' },
    { key: 'target_geographies', label: 'Target geographies', type: 'textarea' },
    { key: 'deal_size', label: 'Target deal/ticket size', type: 'text' },
    { key: 'origination', label: 'Deal origination', type: 'textarea' },
    { key: 'credit_analysis', label: 'Credit analysis process', type: 'textarea' },
    { key: 'covenants', label: 'Covenant package approach', type: 'textarea' },
    { key: 'default_management', label: 'Default management and workout process', type: 'textarea' },
  ]
};

// Infrastructure specific
const infraStrategy: DDQSection = {
  key: 'investment_strategy',
  title: 'Investment Strategy',
  description: 'Details of your infrastructure investment approach.',
  questions: [
    { key: 'strategy_description', label: 'Strategy description', type: 'textarea', required: true },
    { key: 'asset_types', label: 'Asset types', type: 'multiselect', options: ['Transport', 'Energy', 'Renewables', 'Digital/Telecoms', 'Social infrastructure', 'Water/Utilities', 'Other'] },
    { key: 'geographies', label: 'Target geographies', type: 'textarea' },
    { key: 'risk_profile', label: 'Risk profile', type: 'select', options: ['Core', 'Core-plus', 'Value-add', 'Greenfield'] },
    { key: 'deal_size', label: 'Target deal size (equity)', type: 'text' },
    { key: 'contract_types', label: 'Contract/revenue types', type: 'textarea', placeholder: 'Availability-based, volume-based, merchant etc' },
    { key: 'asset_management', label: 'Asset management approach', type: 'textarea' },
    { key: 'leverage_policy', label: 'Leverage policy', type: 'textarea' },
  ]
};

// Fund level sections
const fundOverview: DDQSection = {
  key: 'fund_overview',
  title: 'Fund Overview',
  description: 'Specific details about this fund.',
  questions: [
    { key: 'fund_legal_name', label: 'Fund legal name', type: 'text', required: true },
    { key: 'fund_inception', label: 'Fund inception date', type: 'text' },
    { key: 'fund_aum', label: 'Fund AUM (USD mm)', type: 'text' },
    { key: 'hard_cap', label: 'Hard cap', type: 'text' },
    { key: 'soft_close', label: 'Soft close status', type: 'select', options: ['Open', 'Soft closed', 'Hard closed', 'Raising'] },
    { key: 'num_investors', label: 'Number of investors', type: 'text' },
    { key: 'investor_types', label: 'Investor type breakdown', type: 'textarea', placeholder: 'e.g. 40% pension funds, 30% family offices, 30% endowments' },
    { key: 'largest_investor_concentration', label: 'Largest single investor as % of AUM', type: 'text' },
    { key: 'top_5_concentration', label: 'Top 5 investors as % of AUM', type: 'text' },
  ]
};

const fundTerms: DDQSection = {
  key: 'fund_terms',
  title: 'Fund Terms',
  description: 'Legal and commercial terms of the fund.',
  questions: [
    { key: 'fund_structure', label: 'Fund structure', type: 'select', options: ['Cayman LP', 'Delaware LP', 'Irish LP', 'Luxembourg RAIF', 'UCITS', 'SMA', 'Other'] },
    { key: 'management_fee', label: 'Management fee', type: 'text', placeholder: 'e.g. 2% p.a. on NAV' },
    { key: 'performance_fee', label: 'Performance fee / carried interest', type: 'text', placeholder: 'e.g. 20% above 8% hurdle with catch-up' },
    { key: 'hurdle_rate', label: 'Hurdle rate', type: 'text' },
    { key: 'high_water_mark', label: 'High water mark', type: 'boolean', options: ['Yes', 'No'] },
    { key: 'minimum_investment', label: 'Minimum investment', type: 'text' },
    { key: 'subscription_frequency', label: 'Subscription frequency', type: 'text', placeholder: 'e.g. Monthly with 5 business days notice' },
    { key: 'redemption_frequency', label: 'Redemption frequency', type: 'text' },
    { key: 'redemption_notice', label: 'Redemption notice period', type: 'text' },
    { key: 'lockup', label: 'Lockup period', type: 'text' },
    { key: 'gates', label: 'Gate provisions', type: 'textarea' },
    { key: 'side_pockets', label: 'Side pocket provisions', type: 'textarea' },
    { key: 'most_favoured_nation', label: 'MFN clause available', type: 'boolean', options: ['Yes', 'No'] },
    { key: 'side_letters', label: 'Side letters policy', type: 'textarea' },
  ]
};

const fundDocuments: DDQSection = {
  key: 'fund_documents',
  title: 'Documents & Reporting',
  description: 'Available documentation and investor reporting.',
  questions: [
    { key: 'ppm_available', label: 'PPM / IM available', type: 'boolean', options: ['Yes', 'No'] },
    { key: 'audited_accounts', label: 'Audited annual accounts available', type: 'boolean', options: ['Yes', 'No'] },
    { key: 'audit_firm', label: 'Audit firm', type: 'text' },
    { key: 'nav_frequency', label: 'NAV calculation frequency', type: 'select', options: ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Annually'] },
    { key: 'investor_reporting', label: 'Investor reporting frequency and content', type: 'textarea', placeholder: 'e.g. Monthly factsheet, quarterly letter, annual report' },
    { key: 'transparency', label: 'Portfolio transparency', type: 'textarea', placeholder: 'What portfolio data is shared with investors?' },
    { key: 'isda_available', label: 'ISDA/GMRA available (if applicable)', type: 'boolean', options: ['Yes', 'No', 'N/A'] },
  ]
};

export function getDDQSections(assetClass: string, type: 'firm' | 'fund'): DDQSection[] {
  if (type === 'fund') {
    return [fundOverview, fundTerms, fundDocuments];
  }

  const common = [firmOverview, keyPeople, operations, compliance, esg];

  switch (assetClass) {
    case 'hedge_fund':
      return [firmOverview, keyPeople, hedgeFundStrategy, hedgeFundRisk, operations, compliance, esg];
    case 'pe':
      return [firmOverview, keyPeople, peStrategy, peTrackRecord, operations, compliance, esg];
    case 'vc':
      return [firmOverview, keyPeople, vcStrategy, peTrackRecord, operations, compliance, esg];
    case 'real_estate':
      return [firmOverview, keyPeople, reStrategy, operations, compliance, esg];
    case 'credit':
      return [firmOverview, keyPeople, creditStrategy, operations, compliance, esg];
    case 'infrastructure':
      return [firmOverview, keyPeople, infraStrategy, operations, compliance, esg];
    default:
      return common;
  }
}